<template>
  <div
    ref="inputSelect"
    class="input-select"
    :class="{
      'new-design': newDesign,
      open: showAutocompleteOptions,
    }"
    @keydown="onRootKeydown"
  >
    <div
      class="tw:relative"
      :class="{
        'tw:animate-pulse': loading && newDesign,
      }"
      data-testid="input-select-container"
    >
      <BaseInputText
        :id="computedId"
        ref="inputText"
        :aria-activedescendant="ariaActiveDescendant"
        :aria-autocomplete="ariaAutocomplete"
        :aria-controls="showAutocompleteOptions ? listboxId : undefined"
        :aria-describedby="errors.length ? errorId : undefined"
        :aria-expanded="hideAutoComplete ? undefined : showAutocompleteOptions"
        :aria-haspopup="
          hideAutoComplete ? undefined : isGrid ? 'grid' : 'listbox'
        "
        :aria-invalid="errors.length ? true : undefined"
        :aria-label="ariaLabel || undefined"
        :aria-required="isRequired ? true : undefined"
        :auto-focus="autoFocus"
        :autocomplete="autocomplete"
        class="relative"
        :clearable="clearable"
        :description="description"
        :disabled="disabled"
        :input-classes="inputClasses"
        :label="label"
        :model-value="displayText"
        :new-design="newDesign"
        :optional="optional"
        :placeholder="placeholder"
        :readonly="!useInput"
        :required="isRequired || undefined"
        :role="comboboxRole"
        :validations="[]"
        @blur="onBlur"
        @clear="clear"
        @click.stop="onClick"
        @focus="onFocus"
        @keydown="onInputKeydown"
        @keyup.stop="handleAutoFill"
        @submit="submit"
        @update:model-value="handleInput"
      >
        <template
          v-for="(_, name) in inputTextSlots($slots)"
          #[name]="slotData: unknown"
        >
          <slot
            :name="name"
            v-bind="forwardedInputTextSlotProps(slotData)"
          />
        </template>
        <template #errors>
          <div
            v-if="errors.length"
            :id="errorId"
            class="field-error q-mt-sm"
            :data-testid="errorId"
            role="alert"
          >
            {{ errors[0] }}
          </div>
        </template>
        <template
          v-if="!hideAutoComplete && !hideDropdownButton"
          #append
        >
          <slot name="append">
            <template v-if="newDesign">
              <BaseButton
                :id="dropdownButtonId"
                :active="focused"
                active-color="secondary"
                :aria-expanded="showAutocompleteOptions"
                :aria-label="dropdownButtonLabel"
                :aria-labelledby="dropdownButtonLabelledBy"
                color="form"
                data-testid="dropdown-button"
                :disabled="disabled"
                :icon="dropdownIconName"
                :loading="loading"
                new-design
                tabindex="-1"
                @click.stop="onClick"
              />
            </template>

            <template v-else>
              <QIcon
                class="cursor-pointer"
                :name="dropdownIconName"
                size="16px"
                @click.stop="onClick"
              />

              <QSpinner
                v-if="loading"
                data-testid="spinner"
              />
            </template>
          </slot>
        </template>

        <template #after>
          <Teleport
            :disabled="!teleport"
            :to="teleport"
          >
            <!-- Keydown is bound here as well as on the root because a
            teleported popup is not a DOM descendant of the root, so Escape
            pressed on an option inside it never bubbles to that listener. -->
            <Popper
              v-if="!hideAutoComplete"
              ref="popper"
              v-model="showAutocompleteOptions"
              click-outside-capture
              :click-outside-middleware="shouldCloseList"
              data-testid="input-select-popper"
              flip
              :min-width="popperMinWidth"
              placement="bottom-start"
              :root-element="inputSelect"
              :same-width="!popperWidth"
              :width="popperWidth"
              @keydown="onRootKeydown"
            >
              <slot
                name="autocomplete"
                :on-select="selectOption"
              >
                <BaseAutocompleteList
                  ref="autocompleteList"
                  v-model:show="showAutocompleteOptions"
                  :aria-label="listboxAriaLabel"
                  :aria-labelledby="listboxLabelId"
                  closes-on-tab
                  :is-active-keyboard-only="isActiveKeyboardOnly"
                  :is-grid="isGrid"
                  :is-roving-focus-enabled="isRovingFocusEnabled"
                  :list-id="listboxId"
                  :multiple="Boolean(multiple)"
                  :option-label="optionLabel"
                  :option-prop="optionProp"
                  :options="options"
                  owner-returns-focus
                  :search-el="searchEl"
                  :selection="selection"
                  @select="selectOption"
                  @tab-out="onListTabOut"
                >
                  <template
                    v-for="(_, name) in autocompleteListSlots($slots)"
                    #[name]="slotData: unknown"
                  >
                    <slot
                      :name="name"
                      v-bind="forwardedAutocompleteSlotProps(slotData)"
                    />
                  </template>
                </BaseAutocompleteList>
              </slot>
            </Popper>
          </Teleport>
        </template>
      </BaseInputText>

      <!-- Loading bottom border -->
      <div
        v-if="loading && newDesign"
        class="tw:absolute tw:bottom-0 tw:left-0 tw:right-0 tw:h-0.5 tw:animate-pulse tw:rounded-b-md tw:bg-gradient-to-r tw:from-blue-500 tw:via-blue-400 tw:to-blue-500"
        data-testid="loading-bottom-border"
      />
    </div>
  </div>
</template>

<script lang="ts">
interface InputSelectOption {
  notSelectable?: boolean;
}
</script>

<script
  setup
  lang="ts"
  generic="
    T extends GenericOption & InputSelectOption,
    IsEmittingValue extends boolean = false,
    IsEmittingMultiple extends boolean = false
  "
>
import {
  computed,
  type CSSProperties,
  getCurrentInstance,
  nextTick,
  ref,
  type SlotsType,
  type TeleportProps,
  toRef,
  useId,
  useTemplateRef,
  watch,
} from "vue";
import type {
  ComponentExposed,
  ComponentSlots,
} from "vue-component-type-helpers";

import { getLocaleText } from "shared/boot/i18n";
import BaseAutocompleteList, {
  type AutocompleteListProps,
  type GenericOption,
  isOptionObject,
} from "shared/components/base/BaseAutocompleteList";
import BaseButton from "shared/components/base/BaseButton";
import BaseInputText, {
  type InputTextSubmitEvent,
} from "shared/components/base/BaseInputText.vue";
import Popper from "shared/components/base/BasePopper";
import type { Validations } from "shared/composables/useValidation";
import useValidation from "shared/composables/useValidation";
import { type Maybe, type Nullable } from "shared/types/utility";

interface Props extends Omit<AutocompleteListProps<T>, "selection"> {
  ariaLabel?: string;
  autocomplete?: string;
  autoFocus?: boolean;
  clearable?: boolean;
  closeOnSelect?: boolean;
  debounce?: number;
  description?: string;
  disabled?: boolean;
  dropdownText?: string;
  emitValue?: IsEmittingValue | boolean;
  hasPrependContent?: boolean;
  hideAutoComplete?: boolean;
  hideDropdownButton?: boolean;
  id?: string;
  inputClasses?: string | string[] | Record<string, boolean>;
  isObjectSelection?: boolean;
  label?: string;
  multiple?: IsEmittingMultiple | boolean;
  newDesign?: boolean;
  optional?: boolean;
  placeholder?: string;
  popperMinWidth?: Nullable<number>;
  popperWidth?: Nullable<number>;
  preventClearInputOnSubmit?: boolean;
  required?: boolean;
  selectedText?: ((count: number) => string) | null;
  teleport?: TeleportProps["to"];
  useInput?: boolean;
  validations?: Validations;
}

type EmittedValueType = T extends object ? T[keyof T] : never;

type BaseValueType = IsEmittingValue extends true ? EmittedValueType : T;

type ModelType = IsEmittingMultiple extends true
  ? BaseValueType[]
  : BaseValueType;

type AutocompleteListSlots = ComponentSlots<typeof BaseAutocompleteList<T>>;
type InputTextSlots = ComponentSlots<typeof BaseInputText>;
type ForwardedInputTextSlotProps = {
  class: string | string[] | Record<string, boolean>;
  dirty: boolean;
  errors: string[];
  focused: boolean;
  style: CSSProperties;
};
type ForwardedAutocompleteSlotProps = {
  active: boolean;
  disabled: boolean;
  isFocused: boolean;
  keyboardActive: boolean;
  option: T;
  select: (option: T) => void;
  selected: boolean;
};

// The dynamic template binding types slot data as `unknown`, so assert the
// upstream contract once here instead of re-validating every key.
function forwardedInputTextSlotProps(
  slotData: unknown
): ForwardedInputTextSlotProps {
  if (!slotData || typeof slotData !== "object") {
    return {} as ForwardedInputTextSlotProps;
  }

  return slotData as ForwardedInputTextSlotProps;
}

function forwardedAutocompleteSlotProps(
  slotData: unknown
): ForwardedAutocompleteSlotProps {
  if (!slotData || typeof slotData !== "object") {
    return {} as ForwardedAutocompleteSlotProps;
  }

  return slotData as ForwardedAutocompleteSlotProps;
}

function isEmittedValueArray(
  value: T | T[] | EmittedValueType | EmittedValueType[] | null
): value is EmittedValueType[] {
  return props.emitValue === true && Array.isArray(value);
}

function isBaseValueArray(
  value: BaseValueType | BaseValueType[] | null
): value is BaseValueType[] {
  return Array.isArray(value);
}

const model = defineModel<ModelType | null>({
  default: null,
});

const inputTextModel = defineModel<string>("inputText");
const instance = getCurrentInstance();

const inputTextModelPropKeys = [
  "inputText",
  "input-text",
  "onUpdate:inputText",
  "onUpdate:input-text",
];

const hasInputTextModel = inputTextModelPropKeys.some((key) =>
  Object.prototype.hasOwnProperty.call(instance?.vnode.props ?? {}, key)
);

const props = withDefaults(defineProps<Props>(), {
  ariaLabel: "",
  autocomplete: undefined,
  validations: () => [],
  options: () => [],
  optionProp: "id",
  optionLabel: "",
  label: "",
  description: "",
  placeholder: "",
  dropdownText: "",
  id: "",
  popperMinWidth: null,
  popperWidth: null,
  selectedText: null,
  debounce: 300,
  teleport: null,
  inputClasses: () => [],
});

const emit = defineEmits<{
  blur: [];
  clear: [];
  click: [];
  "dropdown-closed": [];
  "dropdown-opened": [];
  filter: [string];
  focus: [];
  selected: [T];
  submit: [string];
}>();

const slots = defineSlots<
  AutocompleteListSlots &
    InputTextSlots & {
      autocomplete?: (props: { onSelect: typeof selectOption }) => unknown;
    }
>();

const {
  errors,
  dirty,
  isValid,
  validate: validateField,
  reset: resetField,
} = useValidation({
  modelValue: model,
  validations: toRef(() => props.validations),
  optional: toRef(() => props.optional),
});

const showAutocompleteOptions = ref(false);
const focused = ref(false);
const inputTimer = ref<ReturnType<typeof setTimeout>>();
const filter = ref("");
const autoId = useId();

const inputSelect = ref<HTMLDivElement>();
const inputText = ref<ComponentExposed<typeof BaseInputText>>();
const computedId = computed<string>(() => props.id || autoId);
const errorId = computed(() => `${computedId.value}-error`);

const isRequired = computed<boolean>(
  () =>
    props.required ||
    (!props.optional && props.validations.includes("notBlank"))
);

const autocompleteList =
  useTemplateRef<ComponentExposed<typeof BaseAutocompleteList<T>>>(
    "autocompleteList"
  );

const popper = useTemplateRef<ComponentExposed<typeof Popper>>("popper");

// `teleport` renders the popup outside `inputSelect`, so the trigger is not a
// boundary that finds it. The Popper's own element is, either way.
function popperEl(): Nullable<HTMLElement> {
  return popper.value?.element ?? null;
}

// The browser moves focus once this returns, but Vue only removes the popup on
// the next render. Inert it first, or Tab steps back into a control that is
// about to unmount and focus lands on the body.
function onListTabOut() {
  const popup = popperEl();

  if (popup) {
    popup.inert = true;
  }

  focus();
}

function hideList() {
  if (props.disabled) return;

  showAutocompleteOptions.value = false;
}

function shouldCloseList(event: Event) {
  const { target } = event;

  if (target instanceof Node) {
    if (inputText.value?.el?.contains(target)) {
      return false;
    }

    const popperElements = document.querySelectorAll(".base-popper-element");

    if (Array.from(popperElements).some((popper) => popper.contains(target))) {
      return false;
    }
  }

  return true;
}

const dropdownIconName = computed(() => {
  if (props.newDesign)
    return showAutocompleteOptions.value
      ? "keyboard_arrow_up"
      : "keyboard_arrow_down";

  return showAutocompleteOptions.value ? "arrow_drop_up" : "arrow_drop_down";
});

const ariaActiveDescendant = computed<Maybe<string>>(() => {
  if (props.hideAutoComplete || !showAutocompleteOptions.value) {
    return undefined;
  }

  return autocompleteList.value?.activeOptionId ?? undefined;
});

const comboboxRole = computed(() =>
  props.hideAutoComplete ? undefined : "combobox"
);

const ariaAutocomplete = computed(() => {
  if (props.hideAutoComplete || !props.useInput) {
    return undefined;
  }

  return "list";
});

const listboxId = computed(() =>
  props.hideAutoComplete ? undefined : `${computedId.value}-listbox`
);

const listboxLabelId = computed(() =>
  props.label ? `${computedId.value}-label` : undefined
);

// A visible label names the listbox by reference. Without one, `ariaLabel` is
// the control's only name, so forward it rather than ship an unnamed listbox.
const listboxAriaLabel = computed<Maybe<string>>(() =>
  listboxLabelId.value ? undefined : props.ariaLabel || undefined
);

const dropdownButtonLabel = computed<string>(() => {
  const fieldName = props.ariaLabel || props.label;

  return fieldName
    ? getLocaleText("input_select.dropdown_button_label_for", {
        label: fieldName,
      })
    : getLocaleText("input_select.dropdown_button_label");
});

const dropdownButtonId = computed(() => `${computedId.value}-dropdown-button`);

// A #label slot has no text to interpolate, so append it to the button's own
// name by reference instead.
const dropdownButtonLabelledBy = computed<Maybe<string>>(() =>
  !props.ariaLabel && !props.label && slots.label
    ? `${dropdownButtonId.value} ${computedId.value}-label`
    : undefined
);

function inputTextSlots(slots: SlotsType) {
  return slots as InputTextSlots;
}

function autocompleteListSlots(slots: SlotsType) {
  return slots as AutocompleteListSlots;
}

const selectedOption = computed(() => {
  const modelValue = model.value;

  if (!props.emitValue) {
    return modelValue as T | T[];
  }

  const { optionProp } = props;

  if (props.multiple && isEmittedValueArray(modelValue) && optionProp) {
    return props.options.filter((option) => {
      if (!isOptionObject(option)) return false;

      return modelValue.includes(option[optionProp]);
    });
  }

  return props.options?.find((option) => {
    if (!isOptionObject(option) || !optionProp) return false;

    return option[optionProp] === modelValue;
  });
});

const displayText = computed<string>(() => {
  if (props.dropdownText) return props.dropdownText;

  if (filter.value) return filter.value;

  if (inputTextModel.value) return inputTextModel.value;

  if (
    model.value === null ||
    (props.multiple && isBaseValueArray(model.value) && !model.value.length)
  )
    return "";

  if (
    props.multiple &&
    isBaseValueArray(model.value) &&
    model.value.length &&
    !props.useInput
  ) {
    return props.selectedText
      ? props.selectedText(model.value.length)
      : `${model.value.length} selected`;
  }

  const selected = selectedOption.value;

  if (
    props.optionLabel &&
    selected &&
    !props.multiple &&
    !Array.isArray(selected)
  ) {
    if (typeof props.optionLabel === "function") {
      return props.optionLabel(selected);
    }

    if (isOptionObject(selected)) {
      return String(selected[props.optionLabel] ?? "");
    }
  }

  if (selected && !props.optionLabel && !props.multiple) {
    return String(selected);
  }

  return filter.value;
});

const selection = computed({
  get() {
    return model.value;
  },
  set(value) {
    let inputValue = value;

    if (inputValue !== null) {
      const { optionProp } = props;

      if (props.emitValue && optionProp) {
        if (isEmittedValueArray(value) && props.multiple) {
          inputValue = value.map((opt: EmittedValueType) =>
            isOptionObject(opt) ? opt[optionProp] : opt
          );
        } else if (isOptionObject(value)) {
          inputValue = value[optionProp];
        }
      }
    }

    model.value = inputValue;
  },
});

// Delegate to BaseInputText. Its root wrapper has no tabindex, so focusing the
// element directly did nothing.
function focus() {
  inputText.value?.focus();
}

function blur() {
  inputText.value?.blur();
}

function isSelectedOption(option: T, selectedOption: BaseValueType): boolean {
  if (props.optionProp && isOptionObject(option)) {
    const optionValue = option[props.optionProp];

    if (props.emitValue && !props.isObjectSelection) {
      return selectedOption === optionValue;
    }

    if (isOptionObject(selectedOption)) {
      return selectedOption[props.optionProp] === optionValue;
    }
  }

  return JSON.stringify(selectedOption) === JSON.stringify(option);
}

async function selectOption(option: T) {
  if (isOptionObject(option) && option.notSelectable) return;

  const selectionValue = selection.value;

  if (props.multiple && isBaseValueArray(selectionValue)) {
    const index = selectionValue.findIndex((selectedOption: BaseValueType) =>
      isSelectedOption(option, selectedOption)
    );

    if (index !== -1) {
      selection.value = selectionValue.filter(
        (selectedOption: BaseValueType) =>
          !isSelectedOption(option, selectedOption)
      );
    } else {
      selection.value = [...selectionValue, option] as ModelType;
    }

    if (props.closeOnSelect || !props.multiple) {
      hideList();
      focus();
    }
  } else {
    selection.value = option as ModelType;
    await nextTick();

    if (props.closeOnSelect || !props.multiple) {
      hideList();
      focus();
    }
  }

  emit("selected", option);

  if (props.closeOnSelect || !props.multiple) {
    handleInput("");
  }
}

function handleAutoFill(event: KeyboardEvent) {
  if (!event.key) {
    const value = props.options.find((option) => {
      if (!isOptionObject(option) || typeof props.optionLabel !== "string")
        return false;

      return String(option[props.optionLabel] ?? "")
        .toLowerCase()
        .includes((event.target as HTMLInputElement).value.toLowerCase());
    });

    if (value) {
      selectOption(value);
    }
  }
}

function onFocus() {
  focused.value = true;
  emit("focus");
}

function onBlur() {
  focused.value = false;
  emit("blur");
}

function onClick() {
  emit("click");
  toggleList();
}

function closeListAndFocusTrigger(): void {
  hideList();

  nextTick(() => {
    inputText.value?.focus();
  });
}

function onRootKeydown(event: KeyboardEvent): void {
  if (props.hideAutoComplete || props.disabled) {
    return;
  }

  if (event.key === "Escape" && showAutocompleteOptions.value) {
    event.preventDefault();
    event.stopPropagation();
    closeListAndFocusTrigger();
  }
}

function focusListOption(position: "first" | "last"): boolean {
  const options = popperEl()?.querySelectorAll<HTMLElement>(
    '[data-select-option]:not([disabled]):not([aria-disabled="true"])'
  );

  if (!options?.length) {
    return false;
  }

  const option =
    position === "first" ? options[0] : options[options.length - 1];

  // Single-select rows take focus themselves (a <button>); multi-select rows
  // wrap a checkbox, so focus the descendant instead.
  const target = option?.matches("button, input, [tabindex]")
    ? option
    : (option?.querySelector<HTMLElement>(
        'input:not([type="hidden"]), button, [tabindex]'
      ) ?? option);

  target?.focus();

  // Only claim the key when focus actually moved, or a non-focusable row
  // swallows the arrows.
  return document.activeElement === target;
}

// Set when ArrowDown opens the list, so the showAutocompleteOptions watcher can
// activate the first option once BaseAutocompleteList mounts.
let shouldActivateFirstOptionOnOpen = false;

function onInputKeydown(event: KeyboardEvent): void {
  if (props.hideAutoComplete || props.disabled) {
    return;
  }

  if (event.key === "Escape" && showAutocompleteOptions.value) {
    event.preventDefault();
    event.stopPropagation();
    closeListAndFocusTrigger();

    return;
  }

  if (
    (event.key === "ArrowDown" || event.key === "ArrowUp") &&
    showAutocompleteOptions.value &&
    focusListOption(event.key === "ArrowDown" ? "first" : "last")
  ) {
    event.preventDefault();
    event.stopPropagation();

    return;
  }

  if (["ArrowDown", "Enter"].includes(event.key)) {
    event.preventDefault();
    event.stopPropagation();

    // ArrowDown should highlight the first option on this same keypress. Roving
    // focus moves real focus instead (search first), so it opts out.
    shouldActivateFirstOptionOnOpen =
      !props.isRovingFocusEnabled &&
      event.key === "ArrowDown" &&
      !showAutocompleteOptions.value;

    showList();
  }

  if (event.key === " " && !props.useInput) {
    event.preventDefault();
    event.stopPropagation();
    showList();
  }
}

function clear() {
  selection.value = props.multiple ? ([] as ModelType) : null;
  setFilterValue("");
  hideList();
  emit("clear");
}

function toggleList() {
  if (showAutocompleteOptions.value) {
    hideList();
  } else {
    showList();
  }
}

function showList() {
  if (props.disabled) return;

  showAutocompleteOptions.value = true;
}

function handleInput(event: string | null) {
  clearTimeout(inputTimer.value);

  const value = event || "";

  setFilterValue(value);

  if (props.useInput) {
    inputTimer.value = setTimeout(() => {
      emit("filter", value);

      if (event) {
        showList();
      }
    }, props.debounce);
  }
}

function setFilterValue(value: string) {
  filter.value = value;
  inputTextModel.value = value;
}

watch(inputTextModel, (value) => {
  const nextValue = value ?? "";

  if (nextValue !== filter.value) {
    filter.value = nextValue;
  }
});

function submit(payload: InputTextSubmitEvent) {
  // Tab navigates, it does not submit. Close and let focus move on: submitting,
  // clearing the filter or pulling focus back would all trap the user here.
  if (payload.event.key === "Tab") {
    hideList();

    return;
  }

  emit("submit", filter.value);

  if (!props.preventClearInputOnSubmit) {
    handleInput("");
  }

  if (!props.multiple) {
    hideList();
    focus();
  }
}

watch(showAutocompleteOptions, (value) => {
  focused.value = value;

  if (value) {
    emit("dropdown-opened");

    if (shouldActivateFirstOptionOnOpen) {
      shouldActivateFirstOptionOnOpen = false;
      nextTick(() => autocompleteList.value?.activate("first"));
    }

    return;
  }

  emit("dropdown-closed");

  // Focus still inside the closing popup would fall to the body, so hand it back
  // to the trigger. Teleported popups count as inside.
  const activeElement = inputSelect.value?.ownerDocument.activeElement;

  if (
    activeElement &&
    (inputSelect.value?.contains(activeElement) ||
      popperEl()?.contains(activeElement))
  ) {
    nextTick(() => {
      inputText.value?.focus();
    });
  }
});

watch(
  () => props.options,
  () => {
    if (focused.value) {
      showList();
    }
  },
  { deep: true }
);

watch(
  model,
  () => {
    if (hasInputTextModel) return;

    handleInput("");
  },
  { deep: true }
);

defineExpose({
  isValid,
  validate: validateField,
  reset: resetField,
  focus,
  blur,
  hideList,
  errors,
  dirty,
  toggleList,
  setFilterValue,
  autocompleteList,
  // BaseAutocompleteList emits tab-out; fill the #autocomplete slot with your
  // own list and nothing does, so call this when Tab leaves it.
  tabOutOfList: onListTabOut,
  // Moves focus to the first or last option, returning whether it got there so
  // the caller can leave the keypress alone when it did not. The arrows are
  // wired to this component's trigger only, so a custom #autocomplete slot has
  // to call this from its own search field.
  focusListOption,
});
</script>
